import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Globe } from "lucide-react";
import { COMPANY } from "@/data/legal";
import { PHONE_TEL, EMAIL_HREF } from "@/data/services";

const CompanyCard = ({ heading }) => (
  <div
    data-testid="legal-company-card"
    className="mx-auto mt-8 max-w-2xl rounded-2xl border border-[color:var(--rs-border)] bg-[color:var(--rs-panel)] p-6 text-left sm:p-8"
  >
    {heading && (
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--rs-gold)]">
        {heading}
      </p>
    )}
    <p className="font-heading text-xl text-[color:var(--rs-text)]">{COMPANY.name}</p>
    <p className="mt-1 text-sm text-[color:var(--rs-muted)]">CVR: {COMPANY.cvr}</p>
    <div className="mt-4 flex flex-col gap-2 text-sm text-[color:var(--rs-text)]">
      <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 transition-colors hover:text-[color:var(--rs-gold)]">
        <Phone size={15} className="text-[color:var(--rs-gold)]" /> {COMPANY.phone}
      </a>
      <a href={`mailto:${EMAIL_HREF}`} className="inline-flex items-center gap-2 transition-colors hover:text-[color:var(--rs-gold)]">
        <Mail size={15} className="text-[color:var(--rs-gold)]" /> {COMPANY.email}
      </a>
      <span className="inline-flex items-center gap-2">
        <Globe size={15} className="text-[color:var(--rs-gold)]" /> {COMPANY.website}
      </span>
    </div>
  </div>
);

const Section = ({ s }) => (
  <div className="border-t border-[color:var(--rs-border)] pt-8">
    <h2 className="font-heading text-2xl text-[color:var(--rs-text)]">
      {s.n && <span className="mr-2 text-[color:var(--rs-gold)]">{s.n}.</span>}
      {s.title}
    </h2>
    {s.paras?.map((p, i) => (
      <p key={i} className="mt-3 text-sm leading-relaxed text-[color:var(--rs-muted)] sm:text-base">
        {p}
      </p>
    ))}
    {s.items && (
      <ul className="mt-4 space-y-2">
        {s.items.map((it, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed text-[color:var(--rs-text)] sm:text-base">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--rs-gold)]" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    )}
    {s.note && (
      <p className="mt-4 rounded-lg border border-[color:var(--rs-border)] bg-[color:var(--rs-panel2)] px-4 py-3 text-xs leading-relaxed text-[color:var(--rs-muted)]">
        {s.note}
      </p>
    )}
  </div>
);

export default function Legal({ doc }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [doc.slug]);

  return (
    <section className="py-16 lg:py-20">
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <button
          type="button"
          data-testid="back-to-home"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--rs-muted)] transition-colors duration-300 hover:text-[color:var(--rs-gold)]"
        >
          <ArrowLeft size={14} /> Tilbage til forsiden
        </button>

        <div className="mt-8 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[color:var(--rs-gold)]">{doc.kicker}</p>
          <h1
            data-testid={`legal-title-${doc.slug}`}
            className="mt-3 font-heading text-4xl tracking-tight text-[color:var(--rs-text)] lg:text-5xl"
          >
            {doc.title}
          </h1>
          {doc.intro && (
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[color:var(--rs-muted)]">
              {doc.intro}
            </p>
          )}
        </div>

        {doc.showCompany && <CompanyCard heading={doc.companyHeading} />}

        <div className="mt-12 space-y-8">
          {doc.sections.map((s, i) => (
            <Section key={i} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
